import { Modal, Checkbox, Spin, message, Input, InputNumber, Button } from 'antd';
import { Package } from '../../models/Package';
import { useEffect, useState } from 'react';
import { FixedSizeList as List } from 'react-window';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { assignPackagesToReseller, fetchAssignedPackages, fetchPackages } from '../../redux/slices/packageSlice';
interface AssignPackagesModalProps {
  visible: boolean;
  resellerId: number;
  onClose: () => void;
}

export function AssignPackagesModal({ visible, resellerId, onClose }: AssignPackagesModalProps) {
  const SAVED_SELECTION_KEY = "teltrip_saved_packages_selection";
  const dispatch = useDispatch<AppDispatch>();
  const { packages, loading } = useSelector((state: RootState) => state.packages);

  interface SelectedPackage {
    packageId: number;
    packageTemplateId: string;
    adjustedValue: number;
    resellerDiscountPercent: number | null;
  }

  interface SavedSelectionItem {
    packageId?: number;
    prepaidPackageTemplateId?: string;
    packageTemplateId?: string;
    adjustedValue?: number;
    resellerDiscountPercent?: number | null;
    reseller_discount_percent?: number | null;
  }

  const [selectedPackages, setSelectedPackages] = useState<SelectedPackage[]>([]);
  const [savedSelection, setSavedSelection] = useState<SavedSelectionItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [bulkDiscount, setBulkDiscount] = useState<number | null>(null);

  const normalizeDiscount = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const isDiscountInvalid = (value: number | null | undefined) =>
    value !== null && value !== undefined && (value < 0 || value > 100);

  const getResellerDiscountPercent = (pkg: Package) =>
    normalizeDiscount(pkg.resellerDiscountPercent ?? pkg.reseller_discount_percent);

  const formatCurrency = (value: number) => `${value.toFixed(2)}€`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const packagesResult = await dispatch(fetchPackages()).unwrap();
        const assignedResult = await dispatch(fetchAssignedPackages(resellerId)).unwrap();

        const validPackageIds = new Set(packagesResult.map(pkg => pkg.id));
        const packageIdByTemplateId = new Map(
          packagesResult.map(pkg => [pkg.prepaidPackageTemplateId, pkg.id])
        );
        const packageById = new Map(packagesResult.map(pkg => [pkg.id, pkg]));

        // Pre-select assigned packages with current adjustments/discounts
        const selectedPkgs = assignedResult
          .map(pkg => {
            const fallbackId =
              typeof (pkg as Package & { packageId?: number }).packageId === 'number'
                ? (pkg as Package & { packageId?: number }).packageId
                : pkg.prepaidPackageTemplateId
                  ? packageIdByTemplateId.get(pkg.prepaidPackageTemplateId)
                  : undefined;

            const resolvedPackageId = validPackageIds.has(pkg.id) ? pkg.id : fallbackId;

            if (resolvedPackageId === undefined) return null;

            const templateId =
              pkg.prepaidPackageTemplateId ??
              packageById.get(resolvedPackageId)?.prepaidPackageTemplateId;

            if (!templateId) return null;

            return {
              packageId: resolvedPackageId,
              packageTemplateId: templateId,
              adjustedValue: Number(pkg.adjustedValue) || 1,
              resellerDiscountPercent: getResellerDiscountPercent(pkg)
            };
          })
          .filter((pkg): pkg is SelectedPackage => pkg !== null);
        setSelectedPackages(selectedPkgs);
      } catch (error) {
        console.error('Failed to fetch assigned packages', error);
      }
    };
  
    if (visible) {
      fetchData();
      setSearch('');
      setBulkDiscount(null);
    }
  }, [dispatch, visible, resellerId]);
  
  useEffect(() => {
    if (!visible) return;
    const raw = localStorage.getItem(SAVED_SELECTION_KEY);
    if (!raw) {
      setSavedSelection([]);
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setSavedSelection(
          parsed
            .filter(
              item =>
                item &&
                (typeof item.packageId === "number" ||
                  typeof item.prepaidPackageTemplateId === "string")
            )
            .map(item => ({
              packageId: typeof item.packageId === "number" ? item.packageId : undefined,
              prepaidPackageTemplateId:
                typeof item.prepaidPackageTemplateId === "string"
                  ? item.prepaidPackageTemplateId
                  : undefined,
              packageTemplateId:
                typeof item.packageTemplateId === "string"
                  ? item.packageTemplateId
                  : undefined,
              adjustedValue: Number(item.adjustedValue) || 1,
              resellerDiscountPercent: normalizeDiscount(
                item.resellerDiscountPercent ?? item.reseller_discount_percent
              )
            }))
        );
      } else {
        setSavedSelection([]);
      }
    } catch {
      setSavedSelection([]);
    }
  }, [visible]);

  const handleAssign = async () => {
    if (!selectedPackages.length || !resellerId) return;

    if (selectedPackages.some(pkg => isDiscountInvalid(pkg.resellerDiscountPercent))) {
      message.error('Discount must be between 0 and 100');
      return;
    }

    setSaving(true);
    try {
      // Convert selected packages to the format expected by the API
      const packageAssignments = selectedPackages.map(pkg => ({
        packageId: pkg.packageTemplateId,
        adjustedValue: pkg.adjustedValue,
        resellerDiscountPercent: pkg.resellerDiscountPercent ?? 0
      }));

      const resultAction = await dispatch(assignPackagesToReseller({ 
        resellerId, 
        packages: packageAssignments
      }));
  
      if (assignPackagesToReseller.fulfilled.match(resultAction)) {
        message.success('Packages assigned successfully');
        onClose();
      } else {
        message.error(resultAction.payload as string || 'Failed to assign packages');
      }
    } finally {
      setSaving(false);
    }
  };


  const [sortedPackages, setSortedPackages] = useState<Package[]>([]);

  useEffect(() => {
    const filtered = packages
      .filter(pkg => pkg.prepaidPackageTemplateName.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => {
        const aIsSelected = selectedPackages.some(sp => sp.packageId === a.id);
        const bIsSelected = selectedPackages.some(sp => sp.packageId === b.id);
        
        if (aIsSelected && !bIsSelected) return -1; // a comes first
        if (!aIsSelected && bIsSelected) return 1;  // b comes first
        return 0; // maintain original order
      });
    
    setSortedPackages(filtered);
  }, [packages, selectedPackages, search]);

  const allVisibleSelected =
    sortedPackages.length > 0 &&
    sortedPackages.every(pkg =>
      selectedPackages.some(sp => sp.packageId === pkg.id)
    );

  const handleSelectAll = () => {
    setSelectedPackages(prev => {
      const next = new Map(prev.map(pkg => [pkg.packageId, pkg]));
      sortedPackages.forEach(pkg => {
        if (!next.has(pkg.id)) {
          next.set(pkg.id, {
            packageId: pkg.id,
            packageTemplateId: pkg.prepaidPackageTemplateId,
            adjustedValue: 1,
            resellerDiscountPercent: null
          });
        }
      });
      return Array.from(next.values());
    });
  };

  const normalizeSelection = (selection: SavedSelectionItem[]) => {
    const validIds = new Set(packages.map(pkg => pkg.id));
    const packageIdByTemplateId = new Map(
      packages.map(pkg => [pkg.prepaidPackageTemplateId, pkg.id])
    );
    const packageById = new Map(packages.map(pkg => [pkg.id, pkg]));

    const normalized = new Map<number, SelectedPackage>();

    selection.forEach(item => {
      const packageId =
        typeof item.packageId === 'number'
          ? item.packageId
          : typeof item.packageTemplateId === 'string'
            ? packageIdByTemplateId.get(item.packageTemplateId)
            : typeof item.prepaidPackageTemplateId === 'string'
              ? packageIdByTemplateId.get(item.prepaidPackageTemplateId)
            : undefined;

      if (packageId === undefined || !validIds.has(packageId)) return;

      const pkg = packageById.get(packageId);
      if (!pkg) return;

      normalized.set(packageId, {
        packageId,
        packageTemplateId: pkg.prepaidPackageTemplateId,
        adjustedValue: Number(item.adjustedValue) || 1,
        resellerDiscountPercent: normalizeDiscount(
          item.resellerDiscountPercent ?? item.reseller_discount_percent
        )
      });
    });

    return Array.from(normalized.values());
  };

  const handleSaveSelection = () => {
    if (!selectedPackages.length) return;
    const normalized = normalizeSelection(selectedPackages);
    localStorage.setItem(SAVED_SELECTION_KEY, JSON.stringify(normalized));
    setSavedSelection(normalized);
    message.success("Selection saved");
  };

  const handleApplySavedSelection = () => {
    if (!savedSelection.length) return;
    const normalized = normalizeSelection(savedSelection);
    setSelectedPackages(normalized);
    if (!normalized.length) {
      message.warning("Saved selection does not match available packages");
    }
  };

  const handleCheckboxChange = (checked: boolean, pkg: Package) => {
    const id = pkg.id;
    setSelectedPackages((prev) => {
      if (checked) {
        // Add the package with default adjusted value of 1
        return [
          ...prev,
          {
            packageId: id,
            packageTemplateId: pkg.prepaidPackageTemplateId,
            adjustedValue: 1,
            resellerDiscountPercent: null
          }
        ];
      } else {
        // Remove the package
        return prev.filter(item => item.packageId !== id);
      }
    });
    // Note: No need to manually sort here as the useEffect will handle it
  };

  const handleAdjustedValueChange = (value: number | null, id: number) => {
    setSelectedPackages(prev => {
      return prev.map(pkg => {
        if (pkg.packageId === id) {
          return { ...pkg, adjustedValue: value || 1 }; // Default to 1 if null
        }
        return pkg;
      });
    });
  };

  const handleDiscountChange = (value: number | null, id: number) => {
    setSelectedPackages(prev => {
      return prev.map(pkg => {
        if (pkg.packageId === id) {
          return { ...pkg, resellerDiscountPercent: value };
        }
        return pkg;
      });
    });
  };

  const handleApplyBulkDiscount = () => {
    if (!selectedPackages.length) return;
    if (bulkDiscount !== null && isDiscountInvalid(bulkDiscount)) {
      message.error('Discount must be between 0 and 100');
      return;
    }
    setSelectedPackages(prev =>
      prev.map(pkg => ({
        ...pkg,
        resellerDiscountPercent: bulkDiscount
      }))
    );
  };

  const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const pkg = sortedPackages[index];
    const selectedPkg = selectedPackages.find(item => item.packageId === pkg.id);
    const isChecked = !!selectedPkg;
    const discountValue = selectedPkg?.resellerDiscountPercent ?? null;
    const discountError = isDiscountInvalid(discountValue);
    const discountForCalc =
      discountValue === null || discountValue === undefined || Number.isNaN(discountValue)
        ? 0
        : Math.min(Math.max(discountValue, 0), 100);
    const resellerCost = pkg.cost * (1 - discountForCalc / 100);

    return (
      <div
        style={{
          ...style,
          display: 'flex',
          alignItems: 'stretch',
          padding: '8px 12px',
          borderBottom: '1px solid #f0f0f0'
        }}
      >
        <Checkbox
          checked={isChecked}
          onChange={(e) => handleCheckboxChange(e.target.checked, pkg)}
          style={{ flexShrink: 0, marginTop: 4 }}
        />
        <div style={{ marginLeft: 8, flexGrow: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{pkg.prepaidPackageTemplateName}</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {pkg.periodDays}d - {(pkg.dataByte / (1024 * 1024)).toFixed(0)}MB - Base{' '}
            {formatCurrency(pkg.cost)} - Reseller {formatCurrency(resellerCost)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <span style={{ fontSize: 12, color: '#666' }}>Discount %</span>
            <InputNumber
              min={0}
              max={100}
              step={0.01}
              precision={2}
              value={discountValue ?? undefined}
              onChange={(value) => handleDiscountChange(value, pkg.id)}
              style={{ width: 110 }}
              disabled={!isChecked}
              status={discountError ? 'error' : undefined}
            />
            {discountError && (
              <span style={{ color: '#ff4d4f', fontSize: 11 }}>0-100 only</span>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <span style={{ fontSize: 12, color: '#666' }}>Multiplier</span>
            <InputNumber
              min={0.01}
              step={0.01}
              precision={2}
              value={selectedPkg?.adjustedValue ?? 1}
              onChange={(value) => handleAdjustedValueChange(value, pkg.id)}
              style={{ width: 110 }}
              disabled={!isChecked}
              formatter={(value) =>
                value === undefined || value === null ? '' : Number(value).toFixed(2)
              }
              parser={(value) => (value ? Number(value) : 1)}
            />
          </div>
        </div>
      </div>
    );
  };

  const hasDiscountErrors = selectedPackages.some(pkg =>
    isDiscountInvalid(pkg.resellerDiscountPercent)
  );

  return (
    <Modal
      open={visible}
      title="Assign Packages"
      onCancel={onClose}
      onOk={handleAssign}
      okButtonProps={{
        loading: saving,
        disabled: selectedPackages.length === 0 || hasDiscountErrors
      }}
      cancelButtonProps={{ disabled: saving }}
      destroyOnClose
      width={900}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <Input
              placeholder="Search packages..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              onClick={handleSelectAll}
              disabled={sortedPackages.length === 0 || allVisibleSelected}
            >
              Select all
            </Button>
            <InputNumber
              min={0}
              max={100}
              step={0.01}
              precision={2}
              placeholder="Discount %"
              value={bulkDiscount ?? undefined}
              onChange={(value) => setBulkDiscount(value)}
              style={{ width: 140 }}
              status={isDiscountInvalid(bulkDiscount) ? 'error' : undefined}
            />
            <Button
              onClick={handleApplyBulkDiscount}
              disabled={
                selectedPackages.length === 0 ||
                bulkDiscount === null ||
                isDiscountInvalid(bulkDiscount)
              }
            >
              Apply discount to selected
            </Button>
            <Button
              onClick={handleApplySavedSelection}
              disabled={savedSelection.length === 0}
            >
              Apply saved
            </Button>
            <Button
              type="primary"
              onClick={handleSaveSelection}
              disabled={selectedPackages.length === 0}
            >
              Save selection
            </Button>
          </div>

          <div style={{ height: 400, overflow: 'auto', border: '1px solid #eee', borderRadius: 4 }}>
            <List
              height={400}
              itemCount={sortedPackages.length}
              itemSize={88}
              width="100%"
            >
              {Row}
            </List>
          </div>
        </>
      )}
    </Modal>
  );
}
